import { ref, computed, watch, isRef } from 'vue'

export function useRecentSearches(keySrc, opts) {
  const o = opts ?? {}
  const max = Number.isFinite(o.max) ? o.max : 10
  const ttlMs = Number.isFinite(o.ttlHours) ? o.ttlHours * 3600 * 1000 : null
  const storageKind = o.storage === 'local' ? 'localStorage' : 'sessionStorage'

  const storage = safe(storageKind)

  const currentKey = ref(typeof keySrc === 'string' ? keySrc : '')
  const list = ref([])

  if (isRef(keySrc)) {
    watch(
      keySrc,
      (k) => {
        currentKey.value = k || ''
        list.value = load()
      },
      { immediate: true }
    )
  } else {
    list.value = load()
  }

  function safe(which) {
    try {
      return window[which]
    } catch {
      return {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
      }
    }
  }

  function storageKey() {
    const k = currentKey.value || '__default__'
    return `recent:${k}`
  }

  function load() {
    try {
      const raw = storage.getItem(storageKey())
      const arr = raw ? JSON.parse(raw) : []
      const now = Date.now()
      return arr
        .filter((x) => x && typeof x.q === 'string')
        .filter((x) => !ttlMs || now - (x.ts || 0) <= ttlMs)
        .sort((a, b) => (b.ts || 0) - (a.ts || 0))
    } catch {
      return []
    }
  }

  function persist() {
    try {
      storage.setItem(storageKey(), JSON.stringify(list.value.slice(0, max)))
    } catch {}
  }

  const items = computed(() => list.value.map((x) => x.q))

  const normalize = (s) => String(s ?? '').trim().replace(/\s+/g, ' ')
  const keyOf = (s) => normalize(s).toLowerCase()

  function add(term) {
    const q = normalize(term)
    if (!q) return
    const k = keyOf(q)
    const idx = list.value.findIndex((x) => keyOf(x.q) === k)
    if (idx >= 0) list.value.splice(idx, 1)
    list.value.unshift({ q, ts: Date.now() })
    if (list.value.length > max) list.value.length = max
    persist()
  }

  function remove(term) {
    const idx = list.value.findIndex((x) => keyOf(x.q) === keyOf(term))
    if (idx >= 0) {
      list.value.splice(idx, 1)
      persist()
    }
  }

  function clear() {
    list.value = []
    persist()
  }

  function setKey(k) {
    currentKey.value = k || ''
    list.value = load()
  }

  return { items, add, remove, clear, key: currentKey, setKey }
}
